<script>
  import { Link, page, router } from '@inertiajs/svelte'
  import FlashMessage from '../components/flash_message.svelte'
  import LegacyCmsBanner from '../components/legacy_cms_banner.svelte'
  import GlobalSearch from '../components/global_search.svelte'
  import ThemePicker from '../components/theme_picker.svelte'
  import ZuppLogo from '../components/zupp_logo.svelte'
  import Icon from '../components/ui/icon.svelte'
  import Button from '../components/ui/button.svelte'
  import Combobox from '../components/ui/combobox.svelte'
  import { searchHotkeyHint } from '../lib/search.js'
  import { moderationBadge, watchModerationBadge } from '../lib/moderation_badge.svelte.js'
  import { badgeLabel } from '../lib/moderation_live.js'

  let { children } = $props()

  const SECTIONS_KEY = 'admin_site_collapsed_sections'
  const RAIL_KEY = 'admin_site_sidebar_rail'

  const loadStored = (key, fallback) => {
    if (typeof window === 'undefined') return fallback
    try {
      return JSON.parse(window.localStorage.getItem(key)) ?? fallback
    } catch {
      return fallback
    }
  }

  let collapsedSections = $state(loadStored(SECTIONS_KEY, {}))
  let rail = $state(loadStored(RAIL_KEY, false) === true)
  let drawerOpen = $state(false)
  let searchOpen = $state(false)

  // The icon rail is a desktop-only state; the mobile drawer always renders the
  // full sidebar (when it's open, `drawerOpen` forces expanded).
  const expanded = $derived(!rail || drawerOpen)

  // Sections the admin collapsed on this page even though they hold it (the layout remounts per page).
  let collapsedHere = $state({})

  const toggleSection = (section) => {
    const closing = sectionOpen(section)
    collapsedHere = { ...collapsedHere, [section.key]: closing }
    collapsedSections = { ...collapsedSections, [section.key]: closing }
    try {
      window.localStorage.setItem(SECTIONS_KEY, JSON.stringify(collapsedSections))
    } catch {}
  }

  const toggleRail = () => {
    rail = !rail
    window.localStorage.setItem(RAIL_KEY, JSON.stringify(rail))
  }

  const areas = $derived($page.props.areas || [])
  const currentAreaId = $derived($page.props.current_area_id || '')
  const canSwitchArea = $derived(!!$page.props.can_switch_area && areas.length > 0)
  const currentArea = $derived(areas.find((area) => area.id === currentAreaId))
  const areaTooltip = $derived(currentArea ? `College Town: ${currentArea.name}` : 'All College Towns')

  const adminName = $derived($page.props.auth?.admin?.name || $page.props.auth?.admin?.email || 'Admin')
  const adminInitial = $derived(adminName.charAt(0).toUpperCase())

  // The server sends a drilled-in page (detail/edit) back to its section's index.
  const changeArea = (areaId) => {
    router.patch('/current-area', { area_id: areaId }, { preserveScroll: true })
  }

  // The sidebar comes from the server (AdminSite::Sidebar): sections grouped by the job the
  // admin is doing, each item's link and whether this admin may open it. `match` is a
  // page-component prefix so detail/edit pages highlight their nav item too.
  const sections = $derived($page.props.sidebar || [])

  const isActive = (item) => $page.component.startsWith(item.match)
  // A collapsed section still opens while it holds the current page (deep link / reload).
  const sectionOpen = (section) =>
    !collapsedSections[section.key] || (section.items.some(isActive) && !collapsedHere[section.key])

  // Items the admin can't open stay listed, locked with the server's reason.
  const canModerate = $derived(sections.some((section) => section.items.some((item) => item.badge === 'moderation' && item.allowed)))

  $effect(() => {
    if (!canModerate) return
    return watchModerationBadge(() => $page.component.startsWith('moderation/'))
  })

  const badges = $derived({ moderation: canModerate ? moderationBadge.count : null })
  const badgeCount = (item) => (item.badge && badges[item.badge]) || 0

  // Allowed nav pages, flattened for the search palette's "jump to" group.
  const searchPages = $derived(
    sections.flatMap((section) =>
      section.items.filter((item) => item.allowed).map((item) => ({ label: item.label, href: item.url, icon: item.icon, section: section.title }))
    )
  )

</script>

<div class="drawer lg:drawer-open">
  <input id="admin-site-drawer" type="checkbox" class="drawer-toggle" bind:checked={drawerOpen} />

  <div class="drawer-content flex flex-col">
    <LegacyCmsBanner />
    <FlashMessage />

    <div class="navbar bg-base-100 border-b border-base-300 lg:hidden">
      <label for="admin-site-drawer" class="btn btn-square btn-ghost" aria-label="Open menu">
        <Icon name="menu" size="lg" />
      </label>
      <span class="ml-2 flex items-center">
        <ZuppLogo class="h-7 w-auto text-primary" />
      </span>
    </div>

    <main class="flex-1 p-6 bg-base-200 min-h-screen">
      {@render children()}
    </main>
  </div>

  <!-- drawer-side clips overflow; on desktop the rail's account menu opens past its edge. -->
  <div class="drawer-side lg:overflow-visible">
    <label for="admin-site-drawer" aria-label="Close menu" class="drawer-overlay"></label>

    <aside
      class={`bg-base-100 sticky top-0 h-dvh flex flex-col border-r border-base-300 transition-[width] duration-200 ${expanded ? 'w-72' : 'w-20'}`}
    >
      {#if expanded}
        <div class="p-4 border-b border-base-300 flex items-center">
          <ZuppLogo class="h-9 w-auto text-primary flex-1" />
        </div>
        <div class="p-4 border-b border-base-300 space-y-3">
          {#if canSwitchArea}
            <div>
              <label class="fieldset-legend text-xs" for="area-switcher">College Town</label>
              <Combobox
                id="area-switcher"
                value={currentAreaId}
                options={areas.map((area) => ({ value: area.id, label: area.name }))}
                size="sm"
                clearable
                placeholder="All College Towns"
                ariaLabel="College Town"
                onchange={changeArea}
              />
            </div>
          {/if}
          <button
            type="button"
            class="input input-sm w-full flex items-center gap-2 cursor-pointer text-base-content/60"
            onclick={() => (searchOpen = true)}
          >
            <Icon name="search" size="sm" class="shrink-0" />
            <span class="flex-1 text-left">Search</span>
            <kbd class="kbd kbd-xs">{searchHotkeyHint}</kbd>
          </button>
        </div>
      {:else}
        <div class="py-4 border-b border-base-300 flex justify-center">
          <ZuppLogo mark class="h-8 w-auto text-primary" />
        </div>
        <div class="p-2 border-b border-base-300 flex flex-col items-center gap-1">
          {#if canSwitchArea}
            <Button
              variant="ghost"
              size="sm"
              square
              icon="college_towns"
              class={currentArea ? 'text-primary' : 'text-base-content/60'}
              title={`${areaTooltip} — expand to change`}
              aria-label={`${areaTooltip} — expand to change`}
              onclick={toggleRail}
            />
          {/if}
          <Button
            variant="ghost"
            size="sm"
            square
            icon="search"
            title={`Search (${searchHotkeyHint})`}
            aria-label={`Search (${searchHotkeyHint})`}
            onclick={() => (searchOpen = true)}
          />
        </div>
      {/if}

      <nav class="flex-1 overflow-y-auto p-2">
        {#each sections as section, sectionIndex (section.key)}
          {#if expanded}
            <div class="mb-2">
              <button
                type="button"
                class="w-full flex items-center justify-between px-2 py-1 text-xs font-semibold uppercase tracking-wide text-base-content/60"
                onclick={() => toggleSection(section)}
              >
                {section.title}
                <Icon name={sectionOpen(section) ? 'chevron_down' : 'chevron_right'} size="xs" />
              </button>

              {#if sectionOpen(section)}
                <ul class="menu menu-sm w-full">
                  {#each section.items as item (item.url)}
                    <li>
                      {#if item.allowed}
                        <Link href={item.url} class={isActive(item) ? 'bg-primary/10 text-primary font-semibold' : ''}>
                          <Icon name={item.icon} size="sm" class="shrink-0" />
                          <span class="flex-1">{item.label}</span>
                          {#if badgeCount(item) > 0}
                            <span class="badge badge-error badge-sm font-bold" aria-label={`${badgeCount(item)} pending`}>
                              {badgeLabel(badgeCount(item))}
                            </span>
                          {/if}
                        </Link>
                      {:else}
                        <span class="flex items-center gap-2 text-base-content/40" title={item.locked_reason}>
                          <Icon name={item.icon} size="sm" class="shrink-0" />
                          <span class="flex-1">{item.label}</span>
                          <Icon name="lock" size="xs" class="shrink-0" />
                        </span>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {/if}
            </div>
          {:else}
            {#if sectionIndex > 0}
              <div class="divider my-0"></div>
            {/if}
            <ul class="menu w-full p-0">
              {#each section.items as item (item.url)}
                <li>
                  {#if item.allowed}
                    <Link
                      href={item.url}
                      title={badgeCount(item) > 0 ? `${item.label} (${badgeCount(item)})` : item.label}
                      aria-label={item.label}
                      class={`justify-center ${isActive(item) ? 'bg-primary/10 text-primary' : ''}`}
                    >
                      <span class="indicator">
                        {#if badgeCount(item) > 0}
                          <span class="indicator-item status status-error" aria-label={`${badgeCount(item)} pending`}></span>
                        {/if}
                        <Icon name={item.icon} size="sm" />
                      </span>
                    </Link>
                  {:else}
                    <span class="justify-center text-base-content/30" title={item.locked_reason} aria-label={item.locked_reason}>
                      <Icon name={item.icon} size="sm" />
                    </span>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        {/each}
      </nav>

      {#if expanded}
        <div class="p-2 border-t border-base-300 space-y-1">
          <div class="dropdown dropdown-top w-full">
            <button type="button" tabindex="0" class="btn btn-ghost btn-sm w-full justify-start gap-2 font-normal">
              <span class="avatar avatar-placeholder">
                <span class="bg-primary/10 text-primary w-6 rounded-full text-xs">{adminInitial}</span>
              </span>
              <span class="truncate">{adminName}</span>
            </button>
            <ul class="dropdown-content menu bg-base-100 rounded-box shadow border border-base-300 w-full z-10">
              <li>
                <Link href="/account/edit">
                  <Icon name="settings" size="sm" />
                  Account settings
                </Link>
              </li>
              <li>
                <Link href="/sign-out" method="delete" as="button">
                  <Icon name="sign_out" size="sm" />
                  Sign out
                </Link>
              </li>
            </ul>
          </div>
          <div class="px-1 flex items-center justify-between gap-1">
            <ThemePicker />
            <Button
              variant="ghost"
              size="sm"
              square
              icon="sidebar_collapse"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              class="hidden lg:inline-flex"
              onclick={toggleRail}
            />
          </div>
        </div>
      {:else}
        <div class="p-2 border-t border-base-300 flex flex-col items-center gap-1">
          <ThemePicker compact />
          <div class="dropdown dropdown-right dropdown-end">
            <button type="button" tabindex="0" class="btn btn-ghost btn-sm btn-square" title={adminName} aria-label={`Account: ${adminName}`}>
              <span class="avatar avatar-placeholder">
                <span class="bg-primary/10 text-primary w-6 rounded-full text-xs">{adminInitial}</span>
              </span>
            </button>
            <ul class="dropdown-content menu bg-base-100 rounded-box shadow border border-base-300 w-48 z-10 ms-4">
              <li class="menu-title truncate">{adminName}</li>
              <li>
                <Link href="/account/edit">
                  <Icon name="settings" size="sm" />
                  Account settings
                </Link>
              </li>
              <li>
                <Link href="/sign-out" method="delete" as="button">
                  <Icon name="sign_out" size="sm" />
                  Sign out
                </Link>
              </li>
            </ul>
          </div>
          <Button
            variant="ghost"
            size="sm"
            square
            icon="sidebar_expand"
            aria-label="Expand sidebar"
            title="Expand sidebar"
            onclick={toggleRail}
          />
        </div>
      {/if}
    </aside>
  </div>
</div>

<GlobalSearch bind:open={searchOpen} pages={searchPages} scope={currentArea?.name} />
